import { FC, useState } from 'react';
import { Button, Dropdown, InformedField, Modal, Table, Tip } from '@components';
import { Form, FormState, useFieldState, useFormApi, useFormState } from 'informed';
import { useRepository } from '@context';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import {
  IDropdownOption,
  ILocationsOptions,
  LocationDefault,
  Nullable,
  LocationPayload,
  UserDefault,
} from '@common/interfaces';
import { SaveIcon, TrashIcon } from '@assets/svg';
import { GroupBase, OptionsOrGroups } from 'react-select';
import { ColumnType } from 'antd/lib/table';
import css from './styles.module.scss';

interface ILocationModal {
  closeModal: () => void;
  locationData: Nullable<LocationDefault>;
}

interface UserValue {
  first_name: string;
  last_name: string;
  email: string;
  organisation: string;
  uuid: string;
  label: string;
  value: string;
}

interface LocationFormValues {
  country_code: Nullable<IDropdownOption>;
  city_name: Nullable<IDropdownOption>;
  users: UserValue[];
  selectedUser: Nullable<UserValue>;
}

interface IUsersField {
  loadUsersOptions: (
    search: string,
    loadedOptions: OptionsOrGroups<unknown, GroupBase<unknown>>
  ) => Promise<{ options: IDropdownOption[]; hasMore: boolean }>;
  initialUsers: UserValue[];
}

const mapUsers = (users: UserDefault[]) => {
  return users.map(({ uuid, first_name, last_name, email, organisation }) => ({
    label: `${first_name} ${last_name}`,
    value: uuid,
    uuid,
    first_name,
    last_name,
    email,
    organisation: organisation?.short_name || '-',
  }));
};

const UsersField: FC<IUsersField> = ({ loadUsersOptions, initialUsers }) => {
  const formApi = useFormApi();
  const { values } = useFormState();
  const { users = initialUsers } = values as unknown as LocationFormValues;
  const { value } = useFieldState('selectedUser');
  const selectedUser = value as Nullable<UserValue>;

  const [showUserField, setShowUserField] = useState(false);

  const resetField = () => {
    formApi.setValue('selectedUser', null);
    setShowUserField(false);
  };

  const isUserAlreadyAdded = users.some(({ uuid }) => selectedUser?.uuid === uuid);

  const columns: ColumnType<UserDefault>[] = [
    {
      dataIndex: 'first_name',
      key: 'first_name',
      title: 'First name',
    },
    {
      dataIndex: 'last_name',
      key: 'last_name',
      title: 'Last name',
    },
    {
      dataIndex: 'email',
      key: 'email',
      title: 'Email',
    },
    {
      dataIndex: 'organisation',
      key: 'organisation',
      title: 'Organisation',
    },
    {
      dataIndex: 'actions',
      key: 'actions',
      title: '',
      render: (_, user) => (
        <Button
          iconL={<TrashIcon />}
          variant='icon'
          onClick={() =>
            formApi.setValue(
              'users',
              [...users].filter(({ uuid }) => user.uuid !== uuid)
            )
          }
        />
      ),
    },
  ];

  return (
    <InformedField label='Users with access to the customer area'>
      <Table
        emptyText='No users'
        scroll={{ y: 200 }}
        className={css.table}
        rowKey='uuid'
        columns={columns}
        data={users}
        variant='dark'
      />
      {showUserField ? (
        <div className={css.userRow}>
          <Dropdown
            className={css.dropdowm}
            loadOptions={loadUsersOptions}
            isSearchable
            name='selectedUser'
            variant='dark'
          />
          <Tip isVisible={isUserAlreadyAdded} text='User was already added'>
            <Button
              iconL={<SaveIcon />}
              variant='icon'
              disabled={isUserAlreadyAdded}
              onClick={() => {
                if (!selectedUser) return;
                formApi.setValue('users', [...users, selectedUser]);
                resetField();
              }}
            />
          </Tip>
          <Button iconL={<TrashIcon />} variant='icon' onClick={resetField} />
        </div>
      ) : (
        <Button type='button' variant='text' text='Add another user +' onClick={() => setShowUserField(true)} />
      )}
    </InformedField>
  );
};

const LocationModal: FC<ILocationModal> = ({ closeModal, locationData }) => {
  const isNew = !locationData;

  const { accountRepository } = useRepository();

  const { data: options, isLoading: isOptionsLoading } = useQuery<ILocationsOptions>('location-options', () =>
    accountRepository.getLocationsOptions()
  );

  const loadUsersOptions = async (search: string, loadedOptions: OptionsOrGroups<unknown, GroupBase<unknown>>) => {
    const { results, next } = await accountRepository.getUsers({
      limit: 10,
      search,
      offset: loadedOptions.length,
    });

    return {
      options: mapUsers(results),
      hasMore: Boolean(next),
    };
  };

  const { mutate: createLocation, isLoading: inCreationLocationProgress } = useMutation(
    (data: LocationPayload) => accountRepository.createLocation(data),
    {
      onSuccess: () => {
        toast.success('Location was created successfully');
        closeModal();
      },
    }
  );

  const { mutate: patchLocation, isLoading: isPatchingLocationProgress } = useMutation(
    (data: LocationPayload) => accountRepository.patchLocation(locationData?.uuid || '', data),
    {
      onSuccess: () => {
        toast.success('Location was updated successfully');
        closeModal();
      },
    }
  );

  const onSubmit = ({ values }: FormState) => {
    const { country_code, city_name, users } = values as unknown as LocationFormValues;
    const currentUsers = users || locationData?.users || [];
    const baseFormData = {
      country_code: country_code?.value || '',
      city_name: city_name?.value || undefined,
      active_users: currentUsers.map(({ uuid }) => uuid),
    };
    if (isNew) {
      createLocation(baseFormData);
    } else {
      patchLocation(baseFormData);
    }
  };

  const yupSchema = Yup.object().shape({
    country_code: Yup.object().required('Required'),
    city_name: Yup.object(),
    users: Yup.array().notRequired().nullable(),
  });

  const initialValues = {
    city_name: options?.cities.find(({ label }) => label === locationData?.city_name) || null,
    country_code: options?.countries.find(({ label }) => label === locationData?.country_name) || null,
    users: locationData ? mapUsers(locationData.users) : [],
  };

  const loading = isOptionsLoading || inCreationLocationProgress || isPatchingLocationProgress;

  return (
    <Modal
      variant='dark'
      className={css.modal}
      title={isNew ? 'Create new customer area' : 'Edit customer area'}
      closeModal={closeModal}
      loading={loading}
    >
      {options ? (
        <Form onSubmit={onSubmit} yupSchema={yupSchema} initialValues={initialValues}>
          <Modal.Content>
            <div className={css.form}>
              <InformedField label='Country *'>
                <Dropdown
                  isSearchable
                  placeholder='Select country'
                  name='country_code'
                  options={options.countries}
                  variant='dark'
                />
              </InformedField>
              <InformedField label='Workshop (optional)'>
                <Dropdown
                  isSearchable
                  placeholder='Select city'
                  name='city_name'
                  options={options.cities}
                  variant='dark'
                />
              </InformedField>
              {!isNew ? <UsersField loadUsersOptions={loadUsersOptions} initialUsers={initialValues.users} /> : null}
            </div>
          </Modal.Content>
          <Modal.Footer>
            <Button text='Cancel' variant='transparent' onClick={closeModal} />
            {isNew ? (
              <Button text='Create customer area' type='submit' />
            ) : (
              <Button text='Update customer area' variant='forest' type='submit' />
            )}
          </Modal.Footer>
        </Form>
      ) : null}
    </Modal>
  );
};

export default LocationModal;
